import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter, 
  Button, 
  OutlineButton, 
  Input, 
  Select, 
  TextArea, 
  FormGroup, 
  Label, 
  ErrorText, 
  Grid, 
  FlexContainer 
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { ProjectPayment, PaymentType, PaymentMethod } from '../../types';
import apiService from '../../services/api';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormTitle = styled.h1`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin: 1.5rem 0 1rem;
`;

const FileInput = styled.div`
  margin-top: 0.5rem;
  
  input {
    display: none;
  }
  
  label {
    display: inline-block;
    padding: 0.5rem 1rem;
    background-color: var(--light-gray);
    color: var(--dark-gray);
    border-radius: var(--border-radius);
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
      background-color: var(--medium-gray);
    }
  }
`;

const InfoText = styled.p`
  color: var(--dark-gray);
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const PaymentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEditMode = !!id;
  const projectIdFromQuery = searchParams.get('projectId');
  
  const [formData, setFormData] = useState<{
    projectId: string;
    paymentType: PaymentType;
    amount: number;
    currency: string;
    description: string;
    paymentDate: string;
    paymentMethod: PaymentMethod;
    reference?: string;
    attachments: File[];
  }>({
    projectId: projectIdFromQuery || '',
    paymentType: 'Progress',
    amount: 0,
    currency: 'TZS',
    description: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Bank Transfer',
    reference: '',
    attachments: []
  });
  
  const [errors, setErrors] = useState<{
    projectId?: string;
    amount?: string;
    description?: string;
    paymentDate?: string;
  }>({});
  
  const [loading, setLoading] = useState(isEditMode);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  
  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await apiService.getProjects();
        setProjects(response.data || []);
      } catch (error: any) {
        console.error('Error loading projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };
    
    loadProjects();
  }, []);
  
  // Load payment data for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const loadPaymentData = async () => {
        try {
          setLoading(true);
          const response = await apiService.getPayment(id);
          if (response.success && response.data) {
            const payment = response.data;
            setFormData({
              projectId: payment.projectId,
              paymentType: payment.paymentType,
              amount: payment.amount,
              currency: payment.currency,
              description: payment.description,
              paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0],
              paymentMethod: payment.paymentMethod,
              reference: payment.reference || '',
              attachments: []
            });
          }
        } catch (error: any) {
          console.error('Error loading payment data:', error);
        } finally {
          setLoading(false);
        }
      };
      
      loadPaymentData();
    }
  }, [id, isEditMode]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };
  
  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };
  
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    
    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }
    
    if (formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than zero';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        setSubmitLoading(true);
        setSubmitError(null);
        
        const paymentData = {
          ...formData,
          amount: parseFloat(formData.amount.toString()),
          createdBy: user?.name || 'System User' // Include the actual user's name
        };
        
        if (isEditMode) {
          await apiService.updatePayment(id!, paymentData);
        } else {
          await apiService.createPayment(paymentData);
        }
        
        setSubmitSuccess(true);
        setTimeout(() => {
          navigate('/accounting');
        }, 1500);
      } catch (error: any) {
        console.error('Error submitting payment:', error);
        setSubmitError(error.message || 'Failed to submit payment. Please try again.');
      } finally {
        setSubmitLoading(false);
      }
    }
  };
  
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Get project details for display
  const selectedProject = projects.find((p: any) => (p._id || p.id) === formData.projectId);
  
  return (
    <FormContainer>
      <FlexContainer justify="space-between" align="center" style={{ marginBottom: '1.5rem' }}>
        <OutlineButton as={Link} to="/accounting">
          <span style={{ marginRight: '8px' }}>←</span>
          Back to Accounting
        </OutlineButton>
        <FormTitle style={{ margin: 0 }}>{isEditMode ? 'Edit Payment' : 'Add Payment to Project'}</FormTitle>
      </FlexContainer>
      
      <Card>
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <h3>Payment Information</h3>
            {submitError && (
              <div style={{ color: 'var(--danger-color)', marginTop: '0.5rem' }}>
                {submitError}
              </div>
            )}
            {submitSuccess && (
              <div style={{ color: 'var(--success-color)', marginTop: '0.5rem' }}>
                Payment {isEditMode ? 'updated' : 'created'} successfully! Redirecting...
              </div>
            )}
          </CardHeader>
          <CardBody>
            <Grid columns="1fr 1fr" gap="1rem">
              <FormGroup>
                <Label htmlFor="projectId">Project *</Label>
                <Select
                  id="projectId"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  disabled={isEditMode}
                >
                  <option value="">Select a project</option>
                  {projects.map(project => (
                    <option key={project._id || project.id} value={project._id || project.id}>
                      {project.name}
                    </option>
                  ))}
                </Select>
                {errors.projectId && <ErrorText>{errors.projectId}</ErrorText>}
                {selectedProject && (
                  <InfoText>
                    Budget: {formatCurrency(selectedProject.budget.total, selectedProject.budget.currency)}
                  </InfoText>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="paymentType">Payment Type *</Label>
                <Select
                  id="paymentType"
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleInputChange}
                >
                  <option value="Advance">Advance</option>
                  <option value="Progress">Progress</option>
                  <option value="Final">Final</option>
                  <option value="Retention">Retention</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="amount">Amount ({formData.currency}) *</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleInputChange}
                  min="0"
                  step="1000"
                  placeholder="Enter amount"
                  className={errors.amount ? 'error' : ''}
                />
                {errors.amount && <ErrorText>{errors.amount}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                >
                  <option value="TZS">TZS</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="paymentDate">Payment Date *</Label>
                <Input
                  id="paymentDate"
                  name="paymentDate"
                  type="date"
                  value={formData.paymentDate}
                  onChange={handleInputChange}
                  className={errors.paymentDate ? 'error' : ''}
                />
                {errors.paymentDate && <ErrorText>{errors.paymentDate}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="paymentMethod">Payment Method *</Label>
                <Select
                  id="paymentMethod"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                >
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Mobile Money">Mobile Money</option>
                </Select>
              </FormGroup>
            </Grid>
            
            <SectionTitle>Payment Details</SectionTitle>
            
            <FormGroup>
              <Label htmlFor="description">Description *</Label>
              <TextArea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the purpose of this payment"
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <ErrorText>{errors.description}</ErrorText>}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="reference">Reference Number (Optional)</Label>
              <Input
                id="reference"
                name="reference"
                value={formData.reference}
                onChange={handleInputChange}
                placeholder="Enter reference number"
              />
            </FormGroup>
            
            <SectionTitle>Attachments</SectionTitle>
            
            <FormGroup>
              <Label>Upload Documents</Label>
              <FileInput>
                <input
                  type="file"
                  id="attachments"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
                <label htmlFor="attachments">
                  Choose Files
                </label>
              </FileInput>
              <InfoText>
                Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 5MB each)
              </InfoText>
            </FormGroup>
            
            {formData.attachments.length > 0 && (
              <div>
                <h4>Selected Files:</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {formData.attachments.map((file, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'var(--light-gray)',
                      marginBottom: '0.5rem',
                      borderRadius: 'var(--border-radius)'
                    }}>
                      <span>{file.name}</span>
                      <Button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        style={{ 
                          background: 'var(--danger-color)', 
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.8rem'
                        }}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
          <CardFooter>
            <FlexContainer gap="1rem" justify="flex-end">
              <OutlineButton as={Link} to="/accounting" type="button" disabled={submitLoading}>
                Cancel
              </OutlineButton>
              <Button type="submit" disabled={submitLoading}>
                {submitLoading ? 'Processing...' : (isEditMode ? 'Update Payment' : 'Add Payment')}
              </Button>
            </FlexContainer>
          </CardFooter>
        </form>
      </Card>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </FormContainer>
  );
};

export default PaymentForm; 